/**
 * AdminOrders.js — "My Orders" account dashboard page.
 *
 * Lists the logged-in user's order history, fetched from the engine API.
 * Provides search, sort, and pagination controls.
 * Protected — redirects to home if not logged in.
 *
 * Props:
 *  - Good {string}                Time-of-day greeting.
 *  - EngineURL {string}           Base URL for the engine API.
 *  - useLoggedIn {boolean}        Whether the user is authenticated.
 *  - loggedInUserData {object}    Logged-in user details.
 *  - UpdateCartQty {Function}     Updates the cart badge.
 *  - UpdateCartAmount {Function}  Updates the cart total.
 *  - ShowLoginPopup {Function}    Opens the login modal.
 *  - ShowLoginPopup_ {Function}   Redirects to profile if logged in, else shows login.
 */

// ─── External libraries ───────────────────────────────────────────────────────
import React, {useState, useEffect, Fragment, useRef, useCallback} from "react";
import {Link, useHistory, useLocation} from "react-router-dom";
import {SearchIcon, InformationIcon, LoaderIcon} from "../../icons";
import DisclaimerSvg from "../../images/disclaimer.svg";
import ArrowSvg from "../../images/arrow.svg";
import UpArrowSvg from "../../images/up-arrow.svg";
import DownArrowSvg from "../../images/down-arrow.svg";
import {
  Container,
  Row,
  Col,
  ListGroup,
  Form,
  FormControl,
  Button,
  Accordion,
  AccordionButton,
  Modal,
  Table,
  Pagination,
} from "react-bootstrap";
import axios from "axios";
import {Helmet} from "react-helmet";
import {AdminSidebar} from "./Sidebar/AdminSidebar";
import {TermsCondition} from "../Modals/TermsCondition";

export const AdminOrders = (props) => {
  let history = useHistory();
  const My_Account_button_click = () => {
    if (props.loggedInUserData) history.push("/admin-my-library");
    else props.ShowLoginPopup_();
  };

  const [show, setShow] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const location = useLocation();
  const routeLabels = {
    'admin-profile': 'My Profile',
    'admin-my-events': 'Upcoming Events',
    'admin-my-library': 'My Digital Library',
    'admin-cpd-points': 'CPD Points',
    'my-wishlist': 'Bookmarks',
    'admin-orders': 'Billing History',
  };
  const activeLabel = routeLabels[location.pathname.replace('/', '')] || 'Menu';
  const [update, setUpdate] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => {
    setShow(true);
    setHelpText("");
    setHelpCheckbox(false);
    set_form_submitted(false);
  };
  const setUpdatehandleClose = () => setUpdate(false);

  //user details
  const [userEmail, setUserEmail] = useState("");
  const [userLastname, setUserLastname] = useState("");
  const [userToken, setUserToken] = useState(undefined);

  // Orders states
  const [orderDetails, setOrderDetails] = useState([]);
  const [orderLines, setOrderLines] = useState([]);
  const [programRegistrations, setProgramRegistrations] = useState([]);
  const [program_record_types, set_program_record_types] = useState([]);
  const [program_details, set_program_details] = useState([]);
  // Alias kept because the render loop declares a LOCAL `program_details`
  // (the registration) that shadows this state — the program lookup below holds
  // the per-program `recordTypeName` ("Type") and must stay reachable.
  const program_details_lookup = program_details;
  const [contact_details, set_contact_details] = useState([]);
  const [loading, setLoading] = useState(true);
  const search_reference = useRef("");
  const [helpText, setHelpText] = useState("");
  const [helpCheckbox, setHelpCheckbox] = useState(false);
  const [help_text_error, set_help_text_error] = useState("");
  const [help_checkbox_error, set_help_checkbox_error] = useState("");
  const [form_submitted, set_form_submitted] = useState(false);
  const [tc_show, set_tc_show] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── needHelpSubmit — sync validation + disable on submit ─────────────────────
  const needHelpSubmit = () => {
      // Validate synchronously with local vars — not state (React batching issue)
      const err_text     = helpText.trim() === "";
      const err_checkbox = helpCheckbox === false;

      set_help_text_error(err_text ? "Please enter your message." : "");
      set_help_checkbox_error(err_checkbox ? "Please accept the terms & conditions." : "");

      if (err_text || err_checkbox) return;

      send_mail(helpText);
  };
  
  // ── send_mail — with loading state, error handling, POST method ───────────────
  async function send_mail(helpText) {
      setIsSubmitting(true);
      try {
          const response = await axios.post(
              `${props.EngineURL}canwehelp/need-help-email.php`,
              {
                  contact_id: userEmail,
                  sms:        helpText,
                  lanme:      userLastname,
              },
              {
                  headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${userToken}`,
                  },
                  crossdomain: true,
              }
          );
          if (response.data.msg === "Success") {
              set_help_text_error("");
              set_help_checkbox_error("");
              setHelpText("");
              setHelpCheckbox(false);
              set_form_submitted(true);
          } else {
              set_help_text_error("Something went wrong. Please try again.");
          }
      } catch (error) {
          if (error.response?.status === 403) {
              set_help_text_error("Your session has expired. Please log in again.");
          } else if (error.request) {
              set_help_text_error("Network error. Please check your connection.");
          } else {
              set_help_text_error("Something went wrong. Please try again.");
          }
      } finally {
          setIsSubmitting(false);
      }
  }

  const [my_orders, set_my_orders] = useState([]);
  const per_page = 10;
  const [current_page, set_current_page] = useState(1);
  const [total_pages, set_total_pages] = useState(0);
  const [pagination_customize, set_pagination_customize] = useState(0);

  let order_item_index = 0;

  const leo_order_search = () => {
    set_current_page(1);
    let searh_query = search_reference.current.value;
    if (userEmail !== "" && userEmail !== undefined && userLastname !== undefined) {
      setLoading(true);
      getData(searh_query);
    }
  };

  const getData = useCallback(async function getData(searh_query = "") {
    if (userEmail !== "") {
      await axios
        .post(
          `${props.EngineURL}salesforce-query/checkout/leo_orders.php`,
          {
            contact_id:   userEmail,
            searh_query:  searh_query,
            lastname:     userLastname,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userToken}`,
            },
          }
        )
        .then(function (response) {
          if (response.data.error !== undefined && response.data.error !== "") {
            // Error
          } else {
            setOrderDetails(response.data.orders);
            setOrderLines(response.data.order_lines);
            setProgramRegistrations(response.data.program_registrations);
            set_program_record_types(response.data.program_record_types);
            set_program_details(response.data.program_details);
            set_contact_details(response.data.contact_details);
            set_my_orders(response.data.my_orders);
            set_total_pages(Math.ceil(response.data.total_my_orders / per_page));
          }
          setLoading(false);
          return response;
        })
        .catch(function (error) {
          setLoading(false);
        })
        .finally(function () {
          // always executed
        });
    }
  },[props.EngineURL, userEmail, userLastname, userToken]);

  useEffect(() => {
    if (my_orders !== undefined) {
      let total_pages_array = [];
      if (total_pages > 0) {
        for (let order_page = 1; order_page <= total_pages; order_page++) {
          total_pages_array.push(order_page);
          set_pagination_customize(total_pages_array);
        }
      }
    }
  }, [total_pages, my_orders]);

  useEffect(() => {}, [orderDetails, orderLines, programRegistrations]);

  const sortOrder = (by, type) => {
    var sortedOrders = Object.keys(my_orders).sort(function (keyA, keyB) {
      if (by === "Name") {
        if (by === "Name" && type === "desc") {
          return my_orders[keyB][by].localeCompare(my_orders[keyA][by]);
        } else {
          return my_orders[keyA][by].localeCompare(my_orders[keyB][by]);
        }
      } else {
        if (type === "desc") {
          return my_orders[keyB][by] - my_orders[keyA][by];
        } else {
          return my_orders[keyA][by] - my_orders[keyB][by];
        }
      }
    }); //
    let updated_order = {};
    if (sortedOrders.length > 0) {
      sortedOrders.forEach((order_val, order_key) => {
        updated_order[order_val] = my_orders[order_val];
      });
    }
    set_my_orders(updated_order);
  };

  useEffect(() => {
    if (props.loggedInUserData !== undefined && props.loggedInUserData !== null) {
      setUserEmail(props.loggedInUserData.email);
      setUserLastname(props.loggedInUserData.lastName);
      setUserToken(props.loggedInUserData.accessToken);
    }
  }, [props.loggedInUserData]);

  useEffect(() => {
    if (userToken) {
      getData();
    }
  }, [userToken, getData]);

  const doLogout = () => {
    axios
      .post(
        props.EngineURL + "create-account/2fa/delete_2fa.php",
        {
          email: userEmail,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userToken}`, // Replace `yourToken` with your actual token variable
          },
        }
      )
      .then(function (response) {
        localStorage.removeItem("userDetail");
        localStorage.setItem("isLoggedin", false);
        localStorage.removeItem("checkout_token");
        localStorage.removeItem("thank_you_image");
        localStorage.removeItem("checkout_id");
        localStorage.removeItem("cart_session");
        localStorage.removeItem("create_account_detail");
        localStorage.removeItem("cart_session_sku");
        localStorage.removeItem("isLoggedin");
        localStorage.removeItem("was_book");
        localStorage.removeItem("wishlist_item");
        window.location.href = "/";
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  return (
    <>
    <div className="admin_page admin_orders">
      <Helmet>
        <title>My Orders | Leo Cussen Centre for Law</title>
      </Helmet>
      <Container>
        {props.loggedInUserData !== undefined && props.useLoggedIn === true ? (
          <div className="admin_wrapper position-relative">
            {props.loggedInUserData !== null && props.loggedInUserData !== undefined ? (
              <div className="admin_details_block">
                <p className="ae-greeting-text">{props.Good},</p>
                <h1 className="ae-greeting-name">{props.loggedInUserData.firstName}</h1>
              </div>
            ) : null}
            <Row>
              <Col xl={3} lg={4} xs={12} className="d-none d-lg-block">
                <AdminSidebar doLogout={doLogout} />
              </Col>
              <Col xl={9} lg={8} xs={12}>
                <div className="admin_right_content_block">
                  <div className="ae-disclaimer">
                    <div className="ae-disclaimer-icon"><img src={DisclaimerSvg} alt="" /></div>
                    <div className="ae-disclaimer-body">
                      <p className="ae-disclaimer-title">Disclaimer</p>
                      <p className="ae-disclaimer-text">CPD customers please note that order history is retained for the current and previous CPD years (1 April – 31 March). For enquiries contact <a href="mailto:registration@leocussen.edu.au">registration@leocussen.edu.au</a>.</p>
                    </div>
                  </div>
                  <div className="admin_head d-flex flex-wrap align-items-center justify-content-between">
                    <h4 className="m-0">My Orders</h4>
                    <Form
                      className="ae-search-form"
                      onSubmit={(e) => {
                        e.preventDefault();
                        leo_order_search();
                      }}
                    >
                      <div className="ae-search-group">
                        <span className="ae-search-icon"><SearchIcon /></span>
                        <FormControl
                          type="search"
                          placeholder="Start typing"
                          className="ae-search-input"
                          aria-label="Search"
                          ref={search_reference}
                          onChange={(e) => {
                            let search_value = e.target.value;
                            if (search_value === "") {
                              leo_order_search();
                            }
                          }}
                        />
                      </div>
                    </Form>
                  </div>
                  <div className="order_data_wrapper">
                    <div className="order_table_scroll">
                    <div className="order_data_head shadow bg-white rounded-2">
                      <ListGroup horizontal as="ul" className="align-items-center justify-content-between">
                        <ListGroup.Item as="li">
                          Order
                          <div className="shorting_changer">
                            <span className="up_no" onClick={() => sortOrder("Name", "asc")}>
                              <img src={UpArrowSvg} className="sort_arrow_icon" alt="" />
                            </span>
                            <span className="down_no" onClick={() => sortOrder("Name", "desc")}>
                              <img src={DownArrowSvg} className="sort_arrow_icon" alt="" />
                            </span>
                          </div>
                        </ListGroup.Item>
                        <ListGroup.Item as="li">
                          Date
                          <div className="shorting_changer">
                            <span className="up_no" onClick={() => sortOrder("Purshase_Date__seconds", "asc")}>
                              <img src={UpArrowSvg} className="sort_arrow_icon" alt="" />
                            </span>
                            <span className="down_no" onClick={() => sortOrder("Purshase_Date__seconds", "desc")}>
                              <img src={DownArrowSvg} className="sort_arrow_icon" alt="" />
                            </span>
                          </div>
                        </ListGroup.Item>
                        <ListGroup.Item as="li">
                          Subtotal
                          <div className="shorting_changer">
                            <span className="up_no" onClick={() => sortOrder("Subtotal__c", "asc")}>
                              <img src={UpArrowSvg} className="sort_arrow_icon" alt="" />
                            </span>
                            <span className="down_no" onClick={() => sortOrder("Subtotal__c", "desc")}>
                              <img src={DownArrowSvg} className="sort_arrow_icon" alt="" />
                            </span>
                          </div>
                        </ListGroup.Item>
                        <ListGroup.Item as="li">
                          GST
                          <div className="shorting_changer">
                            <span className="up_no" onClick={() => sortOrder("Total_GST__c", "asc")}>
                              <img src={UpArrowSvg} className="sort_arrow_icon" alt="" />
                            </span>
                            <span className="down_no" onClick={() => sortOrder("Total_GST__c", "desc")}>
                              <img src={DownArrowSvg} className="sort_arrow_icon" alt="" />
                            </span>
                          </div>
                        </ListGroup.Item>
                        <ListGroup.Item as="li">
                          Total
                          <div className="shorting_changer">
                            <span className="up_no" onClick={() => sortOrder("Total_Price__c", "asc")}>
                              <img src={UpArrowSvg} className="sort_arrow_icon" alt="" />
                            </span>
                            <span className="down_no" onClick={() => sortOrder("Total_Price__c", "desc")}>
                              <img src={DownArrowSvg} className="sort_arrow_icon" alt="" />
                            </span>
                          </div>
                        </ListGroup.Item>
                        <ListGroup.Item as="li"></ListGroup.Item>
                      </ListGroup>
                    </div>
                    <div className="order_data_listing_block">
                      
                      <Accordion defaultActiveKey="0">
                        {my_orders !== undefined && loading === false && Object.keys(my_orders).length > 0 ? (
                          Object.keys(my_orders).map((order_item, order_values) => {
                            let order = my_orders[order_item];
                            let total_gst = order.Total_GST__c;
                            let purchase_date = order.Purshase_Date__c2;
                            let subtotal = order.Subtotal__c;
                            let total_price = order.Total_Price__c;
                            let order_lines = order.order_lines;
                            let order_lines_length = Object.keys(order_lines).length;
                            order_item_index = order_item_index + 1;
                            let high_order_number = current_page * per_page;
                            let low_order_number = high_order_number - per_page;

                            if (order_item_index > low_order_number && order_item_index <= high_order_number) {
                              return (
                                <Accordion.Item eventKey={order_item_index} className="shadow bg-white rounded-2" key={order_item_index}>
                                  <div className="accordion_header">
                                    <div className="order_head_record_block">
                                      <ListGroup horizontal as="ul" className="align-items-center justify-content-between">
                                        <ListGroup.Item as="li" className="d-flex align-items-center">
                                          <AccordionButton className=""><img src={ArrowSvg} className="order_accordion_arrow" alt="" /></AccordionButton> {order.Shopify_Order_Number__c}
                                        </ListGroup.Item>
                                        <ListGroup.Item as="li">{purchase_date}</ListGroup.Item>
                                        <ListGroup.Item as="li">${parseFloat(subtotal).toFixed(2)}</ListGroup.Item>
                                        <ListGroup.Item as="li">${total_gst}</ListGroup.Item>
                                        <ListGroup.Item as="li">${parseFloat(total_price).toFixed(2)}</ListGroup.Item>
                                        <ListGroup.Item as="li">
                                          <p className="text-center mb-0">
                                            <Link to="#" className="link-item small" onClick={handleShow}>
                                              Need Help?
                                            </Link>
                                          </p>                                          
                                        </ListGroup.Item>
                                      </ListGroup>
                                    </div>
                                  </div>
                                  <Accordion.Body>
                                    {order_lines_length > 0
                                      ? Object.keys(order_lines).map((order_line_key) => {
                                          let program_registrations = order_lines[order_line_key]["program_registrations"];
                                          let program_registrations_length = Object.keys(program_registrations).length;

                                          return (
                                            <Fragment key={order_line_key+Math.random()}>
                                              {program_registrations_length > 0
                                                ? Object.keys(program_registrations).map((program_id) => {
                                                    let program_details = program_registrations[program_id];
                                                    let program_name = program_details.program_name;
                                                    let program_slug = program_details.program_slug !== null ? program_details.program_slug : "";
                                                    let attendy_email = program_details.Attendee_Email__c;
                                                    let Dietary_Requirement = program_details.Dietary_Requirement__c;
                                                    let Registration_Date =
                                                      program_details.Registration_Date__c !== null
                                                        ? program_details.Registration_Date__c.split("-").reverse().join("/")
                                                        : null;
                                                    // "Type" — the local `program_details` here is the REGISTRATION
                                                    // (shadows the state) and has no recordTypeName. Resolve it from
                                                    // the un-shadowed program lookup (state), keyed by program_id,
                                                    // then fall back to the program_record_types lookup.
                                                    const program_info =
                                                      program_details_lookup && program_details_lookup[program_id];
                                                    let recordTypeName =
                                                      (program_info && (program_info.recordTypeName || program_info.RecordTypeName)) ||
                                                      program_details.recordTypeName ||
                                                      program_details.RecordTypeName ||
                                                      "";
                                                    if (!recordTypeName && program_record_types) {
                                                      const rtId =
                                                        program_details.RecordTypeId ||
                                                        program_details.recordTypeId ||
                                                        program_id;
                                                      let resolved;
                                                      if (Array.isArray(program_record_types)) {
                                                        const m = program_record_types.find(
                                                          (rt) => rt && (rt.Id === rtId || rt.id === rtId || rt.RecordTypeId === rtId)
                                                        );
                                                        resolved = m && (m.Name || m.name);
                                                      } else {
                                                        const v = program_record_types[rtId];
                                                        resolved = v && typeof v === "object" ? v.Name || v.name : v;
                                                      }
                                                      recordTypeName = resolved || "";
                                                    }
                                                    let program_fees_details = program_details["program_fees"];
                                                    let program_fee_amount =
                                                      program_fees_details !== undefined && program_fees_details[0] !== undefined && program_fees_details[0]["Amount__c"] !== undefined
                                                        ? program_fees_details[0]["Amount__c"]
                                                        : parseFloat(total_price).toFixed(2);
                                                    let contact_to = program_details["contact__c"];
                                                    let attendy_name =
                                                      contact_details[contact_to] !== undefined && contact_details[contact_to]["totalSize"] > 0
                                                        ? contact_details[contact_to]["records"][0]["Name"]
                                                        : null;
                                                    attendy_email =
                                                      contact_details[contact_to] !== undefined && contact_details[contact_to]["totalSize"] > 0
                                                        ? contact_details[contact_to]["records"][0]["Email"]
                                                        : null;
                                                    // if( contact_to == userid )
                                                    // {
                                                    return (
                                                      <div className="order_details_block" key={program_slug+program_id}>
                                                        <p className="order_program_name">
                                                          <Link to={"/product/" + program_slug}>{program_name}</Link>
                                                        </p>
                                                        <hr className="order_detail_divider" />
                                                        <div className="order_detail_row">
                                                          <div className="order_detail_group">
                                                            <span className="order_detail_label">Attendees</span>
                                                            <span className="order_detail_value">{attendy_name}</span>
                                                          </div>
                                                          <div className="order_detail_group">
                                                            <span className="order_detail_label">Email</span>
                                                            <span className="order_detail_value">{attendy_email}</span>
                                                          </div>
                                                        </div>
                                                        <hr className="order_detail_divider" />
                                                        <div className="order_detail_row order_detail_row--4col">
                                                          <div className="order_detail_group">
                                                            <span className="order_detail_label">Price</span>
                                                            <span className="order_detail_value">${parseFloat(program_fee_amount).toFixed(2)}</span>
                                                          </div>
                                                          <div className="order_detail_group">
                                                            <span className="order_detail_label">Date</span>
                                                            <span className="order_detail_value">{Registration_Date}</span>
                                                          </div>
                                                          <div className="order_detail_group">
                                                            <span className="order_detail_label">Type</span>
                                                            <span className="order_detail_value">{recordTypeName}</span>
                                                          </div>
                                                          <div className="order_detail_group">
                                                            <span className="order_detail_label">Dietary Requirement</span>
                                                            <span className="order_detail_value">{Dietary_Requirement !== null ? Dietary_Requirement : "No"}</span>
                                                          </div>
                                                        </div>
                                                      </div>
                                                    );
                                                    // }
                                                  })
                                                : null}
                                            </Fragment>
                                          );
                                        })
                                      : null}
                                  </Accordion.Body>
                                </Accordion.Item>
                              );
                            }else{
                              return null;
                            }
                          })
                        ) : loading ? (
                          <div className="pre_loader text-center" style={{ position: 'relative', top: 0}}>
                            <LoaderIcon />
                          </div>
                        ) : (
                          <div className="ae-no-events">
                            <p>No records found</p>
                          </div>
                        )}
                      </Accordion>
                    </div>
                    </div>{/* end order_table_scroll */}
                  </div>
                </div>
              </Col>
            </Row>
            {pagination_customize !== undefined && pagination_customize.length > 0 && loading === false && total_pages > 1 ? (
              <Pagination className="justify-content-center flex-wrap">
                {current_page > 1 ? <Pagination.Prev onClick={() => set_current_page(parseFloat(current_page) - parseFloat(1))} /> : null}
                {pagination_customize.map((page_number) => {
                  return (
                    <Pagination.Item key={'page'+page_number} className={page_number === current_page ? "active" : null} onClick={() => set_current_page(page_number)}>
                      {page_number}
                    </Pagination.Item>
                  );
                })}
                {current_page < total_pages ? <Pagination.Next onClick={() => set_current_page(parseFloat(current_page) + parseFloat(1))} /> : null}
              </Pagination>
            ) : null}
          </div>
        ) : (
          <div className="text-center">
            <h3>Please Login</h3>
            <button onClick={() => My_Account_button_click()} className="btn btn-secondary m-0 stest">
              {" "}Login
            </button>
          </div>
        )}
      </Container>
      <>
        <Modal show={show} onHide={handleClose} className="signup_model need_help_modal">
          <Modal.Header closeButton className="border-0 pb-0"></Modal.Header>
          <Modal.Body className="pt-0 px-4">
            {form_submitted === true ? (
              <h5 className="text-center">Thank you for your message. The Registrations Team will attend to your query as soon as possible.</h5>
            ) : (
              <Modal.Title className="text-center">How We Can Help?</Modal.Title>
            )}
            <div className="about_page_wrapper">
              {form_submitted !== true ? (
                <div className="wFormContainer">
                  <Form.Group as={Col} md="12" xs="12" className="form-group">
                    <Form.Label htmlFor="need_help_message">Message *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      id="need_help_message"
                      placeholder="Type your message here"
                      value={helpText}
                      disabled={isSubmitting}
                      className={help_text_error !== "" ? "form-error" : ""}
                      onChange={(e) => { setHelpText(e.target.value); if (help_text_error !== "" && e.target.value !== "") set_help_text_error(""); }}
                    />
                    {help_text_error !== "" ? <span className="field-error-msg">Please enter your message</span> : ""}
                  </Form.Group>
                  <Form.Group as={Col} md="12" xs="12" className="form-group">
                    <Form.Check
                      type="checkbox"
                      id="Checkbox21"
                      label={<>I have read all <span className="text-primary" style={{ textDecoration: "underline", cursor: "pointer" }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); set_tc_show(true); }}>terms &amp; conditions</span></>}
                      onChange={(e) => { setHelpCheckbox(e.target.checked); if (e.target.checked) set_help_checkbox_error(""); }}
                      checked={helpCheckbox}
                      disabled={isSubmitting}
                    />
                    {help_checkbox_error !== "" ? <span className="field-error-msg">Please accept the terms &amp; conditions</span> : ""}
                  </Form.Group>
                  <Form.Group as={Col} xs="12" className="form-group">
                    <Button 
                      variant="primary" 
                      type="button" 
                      disabled={isSubmitting}
                      onClick={() => needHelpSubmit()}>
                        {isSubmitting ? "Sending…" : "Submit"}
                      </Button>
                  </Form.Group>
                </div>
              ) : null}
            </div>
          </Modal.Body>
        </Modal>
      </>
      <>
        <Modal show={update} size="lg" onHide={setUpdatehandleClose} className="update_order_modal">
          <Modal.Header closeButton className="border-0"></Modal.Header>
          <Modal.Body>
            <Modal.Title as="h2" className="h1 text-center">
              Update Your Order
            </Modal.Title>
            <div className="update_order_data">
              <Table>
                <tbody>
                  <tr>
                    <th>Event</th>
                    <td className="text-secondary">Alert! Owners Corporation Act Update 2021 – Legislative reforms and Case Update!</td>
                  </tr>
                  <tr>
                    <th>Atendee</th>
                    <td>John Smith</td>
                  </tr>
                  <tr>
                    <th>Date</th>
                    <td>23/05/2021</td>
                  </tr>
                  <tr>
                    <th>Event Type</th>
                    <td>Seminar</td>
                  </tr>
                  <tr>
                    <th>Price (Inc) GST</th>
                    <td>$300</td>
                  </tr>
                  <tr>
                    <th>Dietary requirement </th>
                    <td>None</td>
                  </tr>
                </tbody>
              </Table>
            </div>
            <Form>
              <Form.Group className="form-group">
                <Form.Label>How we can help?</Form.Label>
                <Form.Select className="form-control">
                  <option value="Refund / Reassign">Refund / Reassign</option>
                  <option value="1">One</option>
                  <option value="2">Two</option>
                  <option value="3">Three</option>
                </Form.Select>
              </Form.Group>
              <div className="information_block card border-exlight">
                <div className="information_content">
                  <InformationIcon />
                  <p className="small mb-0">*all cancellation or program transfer requests are subject to approval</p>
                </div>
              </div>
              <Form.Group className="mb-3" controlId="updateAccountMessage">
                <Form.Label>Message</Form.Label>
                <Form.Control as="textarea" rows={3} />
              </Form.Group>
              <Form.Group controlId="updateAccountcheckbox">
                <Form.Check type="checkbox" label="I have read all terms & conditions" htmlFor="updateAccountcheckbox" required />
              </Form.Group>
              <Button type="submit" className="btn-lg border-2 rounded-2">
                Submit
              </Button>
            </Form>
          </Modal.Body>
        </Modal>
      </>
    </div>
    <div className={`admin_sidebar_mobile${mobileSidebarOpen ? ' sidebar-open' : ''}`}>
      <div className="admin_sidebar_drawer">
        <AdminSidebar doLogout={doLogout} />
      </div>
      <button
        className="admin_sidebar_trigger"
        onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
        aria-expanded={mobileSidebarOpen}
      >
        <span className="admin_sidebar_trigger_label">{activeLabel}</span>
        <svg className="admin_sidebar_trigger_dots" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="5" r="1.5" fill="#1C1C1C"/>
          <circle cx="12" cy="12" r="1.5" fill="#1C1C1C"/>
          <circle cx="12" cy="19" r="1.5" fill="#1C1C1C"/>
        </svg>
      </button>
    </div>
    <TermsCondition show={tc_show} hide={() => set_tc_show(false)} />
    </>
  );
};
